import academicResourceManifest from './academicResourceManifest.js';

// Export the manifest for use in other components
export { academicResourceManifest };

// Utility function to extract class level from stream string
export const getClassLevelFromStream = (stream) => {
  if (!stream) return null;
  const match = stream.match(/Class (\d+)/);
  return match ? Number.parseInt(match[1], 10) : null;
};

// Data mapping function to get class data based on class level
export const getClassData = (classLevel) => {
  const classDataMap = {
    9: Class9Data,
    10: Class10Data,
    // Add more classes as needed
  };
  return classDataMap[classLevel] || Class9Data; // Default to Class 9
};

const Class10Data = {
  className: `ICSE Class 10`,
  board: `ICSE`,
  classLevel: 10,
  textbookAnswers: [
    {
      subjectId: `mathematics`,
      subjectName: `Mathematics`,
      books: [
        {
          id: `ml-aggarwal-maths-10`,
          name: `Understanding ICSE Mathematics (M.L. Aggarwal)`,
          chapters: [
            {
              id: `gst-10`,
              name: `Chapter 1: GST (Goods and Services Tax)`,
              exercises: [
                {
                  id: `ex-1a`,
                  name: `Exercise 1A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `An article is marked at ₹12,000. A dealer sells it to a consumer at a 15% discount. If the rate of GST is 18%, calculate: (i) the selling price of the article, (ii) the CGST paid by the consumer, (iii) the SGST paid by the consumer, (iv) the total amount paid by the consumer.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>(i) Find the Selling Price (SP):</strong><br>
                      Marked Price (MP) = ₹12,000<br>
                      Discount = 15% of MP = <code>(15 / 100) * 12,000 = ₹1,800</code><br>
                      Selling Price (SP) = Marked Price - Discount<br>
                      SP = ₹12,000 - ₹1,800 = <strong>₹10,200</strong><br><br>
                      
                      <strong>(ii) Find the CGST paid by the consumer:</strong><br>
                      Since it is an intra-state transaction, the GST is split equally between the Central Government (CGST) and State Government (SGST).<br>
                      CGST Rate = 18% / 2 = 9%<br>
                      CGST = 9% of Selling Price<br>
                      CGST = <code>(9 / 100) * 10,200 = ₹918</code><br>
                      CGST paid by the consumer = <strong>₹918</strong><br><br>
                      
                      <strong>(iii) Find the SGST paid by the consumer:</strong><br>
                      SGST Rate = 9%<br>
                      SGST = 9% of Selling Price<br>
                      SGST = <code>(9 / 100) * 10,200 = ₹918</code><br>
                      SGST paid by the consumer = <strong>₹918</strong><br><br>
                      
                      <strong>(iv) Find the total amount paid by the consumer:</strong><br>
                      Total Amount = Selling Price + CGST + SGST<br>
                      Total Amount = ₹10,200 + ₹918 + ₹918 = <strong>₹12,036</strong>`
                    },
                    {
                      qNo: `2`,
                      questionText: `A product is sold from Manufacturer A to Dealer B for ₹10,000. Dealer B sells it to Consumer C at a profit of ₹2,000. If the GST rate is 12%, calculate the tax liability of Dealer B to the Government.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Calculate Input Tax Credit (ITC) for Dealer B</strong><br>
                      Cost Price (CP) for Dealer B = ₹10,000<br>
                      Input GST (paid to Manufacturer A) = 12% of ₹10,000 = <code>(12 / 100) * 10,000 = ₹1,200</code><br>
                      Input Tax Credit (ITC) of Dealer B = <strong>₹1,200</strong><br><br>
                      
                      <strong>Step 2: Calculate Output Tax Liability of Dealer B</strong><br>
                      Selling Price (SP) to Consumer C = Cost Price + Profit = ₹10,000 + ₹2,000 = ₹12,000<br>
                      Output GST (collected from Consumer C) = 12% of ₹12,000 = <code>(12 / 100) * 12,000 = ₹1,440</code><br>
                      Output Tax of Dealer B = <strong>₹1,440</strong><br><br>
                      
                      <strong>Step 3: Calculate Net GST payable by Dealer B to the Government</strong><br>
                      Net GST Payable = Output GST - Input Tax Credit (ITC)<br>
                      Net GST Payable = ₹1,440 - ₹1,200 = <strong>₹240</strong>`
                    }
                  ]
                }
              ]
            },
            {
              id: `banking-10`,
              name: `Chapter 2: Banking`,
              exercises: [
                {
                  id: `ex-2a`,
                  name: `Exercise 2A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Karan opens a Recurring Deposit account in a bank and deposits ₹600 per month for 20 months. If the rate of interest is 10% per annum, find the interest earned and the maturity value.`,
                      solution: String.raw`<strong>Step-by-Step Solution:</strong><br><br>
                      Given values:<br>
                      - Monthly Installment ($P$) = ₹600<br>
                      - Number of months ($n$) = 20<br>
                      - Rate of Interest ($r$) = 10% p.a.<br><br>
                      
                      <strong>Step 1: Calculate total equivalent principal for one month ($E_p$).</strong><br>
                      $$E_p = P \cdot \frac{n(n+1)}{2}$$<br>
                      $$E_p = 600 \cdot \frac{20(21)}{2} = 600 \cdot 210 = 1,26,000\text{ rupees}$$<br><br>
                      
                      <strong>Step 2: Calculate interest ($I$) earned on $E_p$.</strong><br>
                      $$I = \frac{E_p \cdot r}{100 \cdot 12}$$<br>
                      $$I = \frac{1,26,000 \cdot 10}{1200} = \frac{12,600}{12} = 1,050\text{ rupees}$$<br>
                      The interest earned is <strong>₹1,050</strong>.<br><br>
                      
                      <strong>Step 3: Calculate the Maturity Value ($M_v$).</strong><br>
                      Maturity Value = Total Principal Deposited + Interest Earned<br>
                      $$M_v = (P \cdot n) + I$$<br>
                      $$M_v = (600 \cdot 20) + 1050 = 12,000 + 1050 = 13,050\text{ rupees}$$<br>
                      The maturity value of the recurring deposit is <strong>₹13,050</strong>.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `physics`,
      subjectName: `Physics`,
      books: [
        {
          id: `selina-physics-10`,
          name: `Concise Physics (Selina Publishers)`,
          chapters: [
            {
              id: `force-10`,
              name: `Chapter 1: Force`,
              exercises: [
                {
                  id: `ex-1a`,
                  name: `Exercise 1A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `A body is pivoted at a point. A force of 10 N is applied at a distance of 30 cm from the pivot. Find the moment of force.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Identify given quantities and convert units to SI.</strong><br>
                      - Force ($F$) = $10\\text{ N}$<br>
                      - Perpendicular distance ($d$) = $30\\text{ cm} = \\frac{30}{100}\\text{ m} = 0.3\\text{ m}$<br><br>
                      
                      <strong>Step 2: Apply the Moment of Force formula.</strong><br>
                      $$\\text{Moment of Force} = \\text{Force} \\cdot \\text{Perpendicular Distance}$$<br>
                      $$\\text{Moment of Force} = F \\cdot d$$<br>
                      $$\\text{Moment of Force} = 10\\text{ N} \\cdot 0.3\\text{ m} = 3\\text{ N m}$$<br>
                      The moment of force is <strong>3 N m</strong>.`
                    },
                    {
                      qNo: `2`,
                      questionText: `State two differences between the centripetal force and centrifugal force.`,
                      solution: `<strong>Comparative Solution:</strong><br><br>
                      <table border="1" cellpadding="5" style="border-collapse: collapse; width:100%; text-align:left; border: 1px solid #ccc;">
                        <thead>
                          <tr style="background-color: #f1f5f9;">
                            <th>Feature</th>
                            <th>Centripetal Force</th>
                            <th>Centrifugal Force</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Nature</strong></td>
                            <td>Real force, acting as a reaction to some physical interaction.</td>
                            <td>Fictitious or pseudo force, arising due to inertia in a rotating frame.</td>
                          </tr>
                          <tr>
                            <td><strong>Direction</strong></td>
                            <td>Directed radially inward, towards the center of the circular path.</td>
                            <td>Directed radially outward, away from the center of the circular path.</td>
                          </tr>
                        </tbody>
                      </table>`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `chemistry`,
      subjectName: `Chemistry`,
      books: [
        {
          id: `selina-chem-10`,
          name: `Concise Chemistry (Selina Publishers)`,
          chapters: [
            {
              id: `periodic-table-10`,
              name: `Chapter 1: Periodic Table, Properties & Variations`,
              exercises: [
                {
                  id: `ex-1a`,
                  name: `Exercise 1A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Define electronegativity and explain why it increases across a period from left to right.`,
                      solution: `<strong>Step-by-Step Explanation:</strong><br><br>
                      <strong>Definition:</strong><br>
                      Electronegativity is the tendency of an atom in a molecule to attract shared pairs of electrons towards itself.<br><br>
                      
                      <strong>Why it increases across a period:</strong><br>
                      1. As we move across a period, the atomic number increases, resulting in an <strong>increase in nuclear charge</strong>.<br>
                      2. Simultaneously, the <strong>atomic size decreases</strong> because electrons are added to the same main shell, and are pulled closer to the nucleus.<br>
                      3. The increased nuclear attraction combined with smaller atomic radius makes it easier for the nucleus to attract shared electrons, causing electronegativity to increase.`
                    },
                    {
                      qNo: `2`,
                      questionText: `State the general trend of electron affinity as we move down a group in the periodic table, and provide reasons.`,
                      solution: `<strong>Step-by-Step Explanation:</strong><br><br>
                      <strong>Trend down a group:</strong><br>
                      Electron affinity generally <strong>decreases</strong> down a group.<br><br>
                      
                      <strong>Reasons:</strong><br>
                      1. <strong>Increase in atomic size:</strong> Down a group, a new energy shell is added at each step, increasing the atomic radius and placing the valence shell further from the nucleus.<br>
                      2. <strong>Shielding effect:</strong> The number of inner shell electrons increases, shielding the outer shell from the nucleus's positive pull.<br>
                      3. Due to these factors, the nuclear attraction on incoming electrons weakens, releasing less energy when an electron is added, thereby decreasing electron affinity.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `biology`,
      subjectName: `Biology`,
      books: [
        {
          id: `selina-bio-10`,
          name: `Concise Biology (Selina Publishers)`,
          chapters: [
            {
              id: `cell-division-10`,
              name: `Chapter 1: Cell Cycle, Cell Division and Structure of Chromosomes`,
              exercises: [
                {
                  id: `ex-1a`,
                  name: `Exercise 1A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `What is the significance of meiosis in sexually reproducing organisms?`,
                      solution: `<strong>Academic Explanation:</strong><br><br>
                      Meiosis is highly significant for the following reasons:<br><br>
                      1. <strong>Maintenance of Chromosome Number:</strong> It reduces the chromosome count in gametes to haploid ($n$). During fertilization, the fusion of haploid male and female gametes restores the diploid ($2n$) status in the zygote, keeping species-specific chromosome counts constant across generations.<br>
                      2. <strong>Crossing Over and Genetic Variation:</strong> During Prophase I, homologous chromosomes pair up and exchange genetic material (crossing over). This rearranges alleles, generating unique genetic combinations that drive evolution.`
                    },
                    {
                      qNo: `2`,
                      questionText: `Describe the events taking place during the Anaphase stage of Mitosis.`,
                      solution: `<strong>Step-by-Step Explanation:</strong><br><br>
                      Anaphase is the shortest phase of mitosis, characterized by these key events:<br><br>
                      1. <strong>Centromere Division:</strong> The centromere connecting the two sister chromatids of each chromosome splits, detaching them into individual daughter chromosomes.<br>
                      2. <strong>Spindle Fiber Contraction:</strong> Spindle fibers attached to kinetochores contract, while non-kinetochore fibers elongate.<br>
                      3. <strong>Polar Migration:</strong> The newly separated sister chromatids are pulled towards opposite poles of the cell, with centromeres leading and chromosome arms trailing behind, creating characteristic 'V', 'J', or 'L' shapes.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `commercial-studies`,
      subjectName: `Commercial Studies`,
      books: [
        {
          id: `kalyani-comstudies-10`,
          name: `ICSE Commercial Studies (Kalyani Publishers)`,
          chapters: [
            {
              id: `stakeholders-10`,
              name: `Chapter 1: Stakeholders in a Commercial Organisation`,
              exercises: [
                {
                  id: `ex-1a`,
                  name: `Exercise 1A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Differentiate between Internal Stakeholders and External Stakeholders in a commercial organization.`,
                      solution: `<strong>Comparative Solution:</strong><br><br>
                      <table border="1" cellpadding="5" style="border-collapse: collapse; width:100%; text-align:left; border: 1px solid #ccc;">
                        <thead>
                          <tr style="background-color: #f1f5f9;">
                            <th>Feature</th>
                            <th>Internal Stakeholders</th>
                            <th>External Stakeholders</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Definition</strong></td>
                            <td>Individuals or groups who are directly inside the organization's structure.</td>
                            <td>Individuals or groups outside the organization but affected by its activities.</td>
                          </tr>
                          <tr>
                            <td><strong>Examples</strong></td>
                            <td>Owners, shareholders, board directors, employees, and managers.</td>
                            <td>Customers, creditors, suppliers, local community, and government bodies.</td>
                          </tr>
                          <tr>
                            <td><strong>Nature of Impact</strong></td>
                            <td>Directly involved in operational decisions and daily processes.</td>
                            <td>Indirectly impact or are impacted by market, economic, and regulatory changes.</td>
                          </tr>
                        </tbody>
                      </table>`
                    },
                    {
                      qNo: `2`,
                      questionText: `Explain two main expectations of the Government from a business organization.`,
                      solution: `<strong>Academic Explanation:</strong><br><br>
                      Two main expectations of the Government from a commercial business organization are:<br><br>
                      1. <strong>Compliance with Rules and Regulations:</strong> The Government expects businesses to strictly adhere to industrial, environmental, labor, and safety laws. This ensures fair competition and prevents unethical practices.<br>
                      2. <strong>Prompt and Fair Payment of Taxes:</strong> Businesses are expected to accurately declare income and pay corporate taxes, excise duties, custom duties, and GST on time. These tax revenues support public infrastructure and development.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    }
  ],
  pastPapers: [
    {
      id: `icse-2024-maths-10`,
      year: 2024,
      subject: `Mathematics`,
      questionPaperDownloadUrl: `https://example.com/downloads/icse-2024-maths.pdf`,
      markingSchemeSolutions: [
        {
          qNo: `1`,
          section: `Section A (40 Marks)`,
          questionText: `A shopkeeper in Delhi sells an item of list price ₹8,000 to a consumer in Mumbai at a discount of 20%. If the rate of GST is 18%, calculate the IGST paid by the shopkeeper.`,
          solution: `<strong>Marking-Scheme-Accurate Solution:</strong><br><br>
          <strong>Step 1: Calculate the Selling Price (SP)</strong><br>
          Marked Price = ₹8,000<br>
          Discount = 20% of ₹8,000 = ₹1,600<br>
          Selling Price (SP) = ₹8,000 - ₹1,600 = ₹6,400<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span><br><br>
          
          <strong>Step 2: Determine transaction nature and calculate IGST</strong><br>
          Since the transaction is inter-state (Delhi to Mumbai), Integrated GST (IGST) is charged at the full rate of 18%:<br>
          IGST = 18% of Selling Price<br>
          IGST = <code>(18 / 100) * 6,400 = ₹1,152</code><br>
          The IGST paid by the shopkeeper is <strong>₹1,152</strong>.<br>
          <span class="marking-scheme-badge">[Allocated Marks: 2]</span>`
        }
      ]
    },
    {
      id: `icse-2023-physics-10`,
      year: 2023,
      subject: `Physics`,
      questionPaperDownloadUrl: `https://example.com/downloads/icse-2023-physics.pdf`,
      markingSchemeSolutions: [
        {
          qNo: `1`,
          section: `Section A (40 Marks)`,
          questionText: `A block and tackle pulley system has a velocity ratio of 5. Calculate its mechanical advantage if its efficiency is 80%.`,
          solution: `<strong>Marking-Scheme-Accurate Solution:</strong><br><br>
          Given:<br>
          - Velocity Ratio (VR) = 5<br>
          - Efficiency ($\eta$) = 80% = 0.80<br><br>
          
          <strong>Step 1: Write down the efficiency formula.</strong><br>
          $$\\eta = \\frac{\\text{MA}}{\\text{VR}}$$ <span class="marking-scheme-badge">[Allocated Marks: 1]</span><br><br>
          
          <strong>Step 2: Rearrange and calculate Mechanical Advantage (MA).</strong><br>
          $$\\text{MA} = \\eta \\cdot \\text{VR}$$<br>
          $$\\text{MA} = 0.80 \\cdot 5 = 4$$<br>
          The Mechanical Advantage of the pulley system is <strong>4</strong>.<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span>`
        }
      ]
    }
  ]
};

const Class9Data = {
  className: `ICSE Class 9`,
  board: `ICSE`,
  classLevel: 9,
  textbookAnswers: [
    {
      subjectId: `mathematics`,
      subjectName: `Mathematics`,
      books: [
        {
          id: `concise-selina-maths-9`,
          name: `Concise Selina Mathematics Class 9`,
          chapters: [
            {
              id: `rational-irrational-9`,
              name: `Chapter 1: Rational and Irrational Numbers`,
              exercises: [
                {
                  id: `ex-1a`,
                  name: `Exercise 1A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Insert a rational number between 1/3 and 1/2, and arrange them in ascending order.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Use the mean method to find a rational number between two fractions.</strong><br>
                      Let $a = \\frac{1}{3}$ and $b = \\frac{1}{2}$.<br>
                      The rational number exactly halfway between them is:<br>
                      $$q = \\frac{a + b}{2} = \\frac{\\frac{1}{3} + \\frac{1}{2}}{2}$$<br>
                      
                      <strong>Step 2: Simplify the numerator by finding a common denominator (6).</strong><br>
                      $$\\frac{1}{3} + \\frac{1}{2} = \\frac{2 + 3}{6} = \\frac{5}{6}$$<br>
                      Now, divide by 2:<br>
                      $$q = \\frac{\\frac{5}{6}}{2} = \\frac{5}{12}$$<br><br>
                      
                      <strong>Step 3: Convert fractions to compare and arrange them.</strong><br>
                      To arrange $\\frac{1}{3}$, $\\frac{5}{12}$, and $\\frac{1}{2}$ in ascending order, convert them to equivalent fractions with a common denominator of 12:<br>
                      - $\\frac{1}{3} = \\frac{4}{12}$<br>
                      - $\\frac{5}{12}$ remains $\\frac{5}{12}$<br>
                      - $\\frac{1}{2} = \\frac{6}{12}$<br><br>
                      
                      Since $4 < 5 < 6$, we have:<br>
                      $$\\frac{1}{3} < \\frac{5}{12} < \\frac{1}{2}$$<br>
                      The rational number is <strong>5/12</strong> and the ascending order is <strong>1/3, 5/12, 1/2</strong>.`
                    },
                    {
                      qNo: `2`,
                      questionText: `Prove that $\\sqrt{2}$ is an irrational number.`,
                      solution: `<strong>Step-by-Step Proof by Contradiction:</strong><br><br>
                      <strong>Step 1: Assume the contrary.</strong><br>
                      Let us assume that $\\sqrt{2}$ is a rational number. Therefore, it can be written in the form:<br>
                      $$\\sqrt{2} = \\frac{p}{q}$$<br>
                      where $p$ and $q$ are integers, $q \\neq 0$, and $p$ and $q$ are co-prime.<br><br>
                      
                      <strong>Step 2: Square both sides of the equation.</strong><br>
                      $$2 = \\frac{p^2}{q^2} \\implies p^2 = 2q^2$$<br>
                      Since $p^2$ is equal to $2q^2$, $p^2$ is an even number. This implies $p$ is an even number.<br><br>
                      
                      <strong>Step 3: Represent p using an even variable and substitute back.</strong><br>
                      Let $p = 2k$, where $k$ is an integer. Substitute this value into the equation:<br>
                      $$(2k)^2 = 2q^2 \\implies 4k^2 = 2q^2 \\implies 2k^2 = q^2$$<br>
                      Since $q^2$ is equal to $2k^2$, $q^2$ is an even number, which means $q$ is also an even number.<br><br>
                      
                      <strong>Step 4: Analyze the contradiction.</strong><br>
                      Both $p$ and $q$ are even, meaning they share a common factor of 2. This directly contradicts our initial assumption that $p$ and $q$ are co-prime.<br><br>
                      
                      <strong>Conclusion:</strong><br>
                      Our assumption that $\\sqrt{2}$ is rational is false. Therefore, $\\sqrt{2}$ is an <strong>irrational number</strong>.`
                    }
                  ]
                }
              ]
            },
            {
              id: `compound-interest-9`,
              name: `Chapter 2: Compound Interest`,
              exercises: [
                {
                  id: `ex-2a`,
                  name: `Exercise 2A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Find the compound interest on ₹10,000 for 2 years at 10% per annum compounded annually.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Calculate the interest for the first year.</strong><br>
                      Principal for the 1st year ($P_1$) = ₹10,000<br>
                      Rate ($R$) = 10% p.a.<br>
                      Interest for the 1st year ($I_1$) = $\\frac{P_1 \\times R \\times 1}{100}$<br>
                      $$I_1 = \\frac{10000 \\times 10 \\times 1}{100} = ₹1,000$$<br><br>
                      
                      <strong>Step 2: Calculate the amount at the end of the first year.</strong><br>
                      Amount at the end of the 1st year ($A_1$) = $P_1 + I_1$<br>
                      $$A_1 = 10000 + 1000 = ₹11,000$$<br><br>
                      
                      <strong>Step 3: Calculate the interest for the second year.</strong><br>
                      Principal for the 2nd year ($P_2$) = $A_1$ = ₹11,000<br>
                      Interest for the 2nd year ($I_2$) = $\\frac{P_2 \\times R \\times 1}{100}$<br>
                      $$I_2 = \\frac{11000 \\times 10 \\times 1}{100} = ₹1,100$$<br><br>
                      
                      <strong>Step 4: Calculate total Compound Interest (CI).</strong><br>
                      Total Interest = $I_1 + I_2$<br>
                      $$\\text{CI} = 1000 + 1100 = ₹2,100$$<br>
                      The compound interest is <strong>₹2,100</strong>.`
                    },
                    {
                      qNo: `2`,
                      questionText: `A sum of money amounts to ₹11,025 in 2 years at 5% per annum compound interest. Find the sum.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Apply the compound interest amount formula.</strong><br>
                      Let the Principal be $P$.<br>
                      Amount ($A$) = $P \\left(1 + \\frac{R}{100}\\right)^n$<br>
                      Given: $A = ₹11,025$, $R = 5\%$, $n = 2$<br><br>
                      
                      <strong>Step 2: Substitute the values and simplify.</strong><br>
                      $$11025 = P \\left(1 + \\frac{5}{100}\\right)^2$$<br>
                      $$11025 = P \\left(1 + \\frac{1}{20}\\right)^2$$<br>
                      $$11025 = P \\left(\\frac{21}{20}\\right)^2$$<br>
                      $$11025 = P \\left(\\frac{441}{400}\\right)$$<br><br>
                      
                      <strong>Step 3: Solve for P.</strong><br>
                      $$P = \\frac{11025 \\times 400}{441}$$<br>
                      Since $11025 \\div 441 = 25$:<br>
                      $$P = 25 \\times 400 = ₹10,000$$<br>
                      The principal sum of money is <strong>₹10,000</strong>.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `physics`,
      subjectName: `Physics`,
      books: [
        {
          id: `concise-selina-physics-9`,
          name: `Concise Selina Physics Class 9`,
          chapters: [
            {
              id: `measurements-9-phy`,
              name: `Chapter 1: Measurements and Experimentation`,
              exercises: [
                {
                  id: `ex-1a`,
                  name: `Exercise 1A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Explain what is meant by the least count of a measuring instrument. How is the least count of a vernier callipers calculated?`,
                      solution: `<strong>Step-by-Step Explanation:</strong><br><br>
                      <strong>Definition of Least Count:</strong><br>
                      The least count of an instrument is the smallest measurement that can be taken accurately with it. It determines the precision of the measuring device.<br><br>
                      
                      <strong>Formula for Least Count of Vernier Callipers:</strong><br>
                      The least count (L.C.), also known as the vernier constant, is the difference between the value of one main scale division (M.S.D.) and one vernier scale division (V.S.D.).<br>
                      $$\\text{L.C.} = 1\\text{ M.S.D.} - 1\\text{ V.S.D.}$$<br>
                      
                      Alternatively, it can be calculated using the formula:<br>
                      $$\\text{L.C.} = \\frac{\\text{Value of 1 Main Scale Division}}{\\text{Total Number of Divisions on Vernier Scale}}$$`
                    },
                    {
                      qNo: `2`,
                      questionText: `A vernier callipers has 10 divisions on its vernier scale which coincide with 9 divisions of the main scale. If 1 division of the main scale is 1 mm, determine its least count in cm.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Identify the given values.</strong><br>
                      - Value of 1 Main Scale Division ($1\\text{ M.S.D.}$) = $1\\text{ mm} = 0.1\\text{ cm}$<br>
                      - Total number of divisions on the vernier scale ($n$) = 10<br><br>
                      
                      <strong>Step 2: Apply the Least Count formula.</strong><br>
                      $$\\text{Least Count (L.C.)} = \\frac{\\text{Value of 1 M.S.D.}}{\\text{Total Divisions on Vernier Scale}}$$<br>
                      $$\\text{L.C.} = \\frac{1\\text{ mm}}{10} = 0.1\\text{ mm}$$<br><br>
                      
                      <strong>Step 3: Convert the unit into centimeters as requested.</strong><br>
                      Since $1\\text{ mm} = 0.1\\text{ cm}$:<br>
                      $$\\text{L.C.} = 0.1\\text{ mm} = 0.01\\text{ cm}$$<br>
                      The least count of the vernier callipers is <strong>0.01 cm</strong>.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `chemistry`,
      subjectName: `Chemistry`,
      books: [
        {
          id: `candid-chemistry-9`,
          name: `Candid Chemistry Class 9`,
          chapters: [
            {
              id: `language-chemistry-9`,
              name: `Chapter 1: The Language of Chemistry`,
              exercises: [
                {
                  id: `ex-1a`,
                  name: `Exercise 1A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Define valency. Write the chemical formula of: (i) Aluminium sulphate, and (ii) Ammonium carbonate.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Definition of Valency:</strong><br>
                      Valency is the combining capacity of an atom of an element or a radical. It is determined by the number of electrons lost, gained, or shared by the atom during a chemical reaction.<br><br>
                      
                      <strong>Deriving Chemical Formulae using Criss-Cross Method:</strong><br><br>
                      
                      <strong>(i) Aluminium sulphate:</strong><br>
                      - Symbols of constituent ions: Aluminium ($\\text{Al}^{3+}$) and Sulphate ($\\text{SO}_4^{2-}$)<br>
                      - Write their charges/valencies: $\\text{Al} = 3$, $\\text{SO}_4 = 2$<br>
                      - Cross over the valencies:<br>
                      - Resulting Formula: <strong>\\text{Al}_2(\\text{SO}_4)_3</strong><br><br>
                      
                      <strong>(ii) Ammonium carbonate:</strong><br>
                      - Symbols of constituent ions: Ammonium ($\\text{NH}_4^+$) and Carbonate ($\\text{CO}_3^{2-}$)<br>
                      - Write their charges/valencies: $\\text{NH}_4 = 1$, $\\text{CO}_3 = 2$<br>
                      - Cross over the valencies:<br>
                      - Resulting Formula: <strong>(\\text{NH}_4)_2\\text{CO}_3</strong>`
                    },
                    {
                      qNo: `2`,
                      questionText: `Balance the following chemical equation: $\\text{KClO}_3 \\rightarrow \\text{KCl} + \\text{O}_2$`,
                      solution: `<strong>Step-by-Step Balancing Process:</strong><br><br>
                      <strong>Step 1: Count atoms on both sides of the unbalanced equation.</strong><br>
                      $$\\text{KClO}_3 \\rightarrow \\text{KCl} + \\text{O}_2$$<br>
                      - Reactant Side (LHS): $\\text{K} = 1, \\text{Cl} = 1, \\text{O} = 3$<br>
                      - Product Side (RHS): $\\text{K} = 1, \\text{Cl} = 1, \\text{O} = 2$<br><br>
                      
                      <strong>Step 2: Balance the Oxygen atoms.</strong><br>
                      The lowest common multiple of 3 and 2 is 6. Place coefficient 2 before $\\text{KClO}_3$ and 3 before $\\text{O}_2$:<br>
                      $$2\\text{KClO}_3 \\rightarrow \\text{KCl} + 3\\text{O}_2$$<br><br>
                      
                      <strong>Step 3: Balance potassium and chlorine atoms.</strong><br>
                      Place a coefficient of 2 before $\\text{KCl}$:<br>
                      $$2\\text{KClO}_3 \\rightarrow 2\\text{KCl} + 3\\text{O}_2$$<br><br>
                      
                      <strong>Step 4: Verify the final balance.</strong><br>
                      - LHS: $\\text{K} = 2, \\text{Cl} = 2, \\text{O} = 6$<br>
                      - RHS: $\\text{K} = 2, \\text{Cl} = 2, \\text{O} = 6$<br>
                      The balanced chemical equation is: <strong>2\\text{KClO}_3 \\rightarrow 2\\text{KCl} + 3\\text{O}_2</strong>`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `biology`,
      subjectName: `Biology`,
      books: [
        {
          id: `concise-selina-biology-9`,
          name: `Concise Selina Biology Class 9`,
          chapters: [
            {
              id: `introducing-biology-9`,
              name: `Chapter 1: Introducing Biology`,
              exercises: [
                {
                  id: `ex-1a`,
                  name: `Exercise 1A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Define biology. List three major branches of biology along with their definitions.`,
                      solution: `<strong>Academic Explanation:</strong><br><br>
                      <strong>Definition:</strong><br>
                      Biology is the branch of science that deals with the study of living organisms, including their physical structure, chemical processes, molecular interactions, physiological mechanisms, development, and evolution.<br><br>
                      
                      <strong>Three Major Branches of Biology:</strong><br>
                      1. <strong>Botany:</strong> The scientific study of plants, including their physiology, structure, genetics, and ecology.<br>
                      2. <strong>Zoology:</strong> The scientific study of animal biology, covering behavior, physiology, classification, and structure.<br>
                      3. <strong>Microbiology:</strong> The study of microscopic organisms, such as bacteria, viruses, fungi, and protozoa.`
                    },
                    {
                      qNo: `2`,
                      questionText: `Differentiate between Botany and Zoology.`,
                      solution: `<strong>Comparative Solution:</strong><br><br>
                      <table border="1" cellpadding="5" style="border-collapse: collapse; width:100%; text-align:left; border: 1px solid #ccc;">
                        <thead>
                          <tr style="background-color: #f1f5f9;">
                            <th>Feature</th>
                            <th>Botany</th>
                            <th>Zoology</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Primary Object</strong></td>
                            <td>Plants and plant life cycles.</td>
                            <td>Animals and animal behaviors.</td>
                          </tr>
                          <tr>
                            <td><strong>Nutrition</strong></td>
                            <td>Studies autotrophic organisms (photosynthetic).</td>
                            <td>Studies heterotrophic organisms (rely on external food).</td>
                          </tr>
                          <tr>
                            <td><strong>Mobility</strong></td>
                            <td>Deals with stationary organisms.</td>
                            <td>Deals with mobile organisms capable of locomotion.</td>
                          </tr>
                        </tbody>
                      </table>`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `commercial-studies`,
      subjectName: `Commercial Studies`,
      books: [
        {
          id: `commercial-studies-cb-gupta-9`,
          name: `Commercial Studies Class 9 (CB Gupta)`,
          chapters: [
            {
              id: `commercial-activities-9`,
              name: `Chapter 1: Commercial Activities`,
              exercises: [
                {
                  id: `ex-1a`,
                  name: `Exercise 1A`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Define commercial activities and differentiate them from non-commercial activities.`,
                      solution: `<strong>Academic Explanation:</strong><br><br>
                      <strong>Commercial Activities:</strong><br>
                      Commercial activities are those human activities that are undertaken with the primary objective of earning a livelihood, generating income, or accumulating wealth. Examples include runnning a business, working in an office, or practicing a profession.<br><br>
                      
                      <strong>Differentiating Parameters:</strong><br>
                      - <strong>Objective:</strong> Commercial activities aim to earn money, whereas non-commercial activities are guided by social, psychological, or emotional considerations (e.g., charity, leisure, or prayer).<br>
                      - <strong>Outcome:</strong> Commercial activities result in the creation of wealth and utilities, while non-commercial activities lead to mental satisfaction or social welfare.`
                    },
                    {
                      qNo: `2`,
                      questionText: `Explain the main characteristics of business as a commercial activity.`,
                      solution: `<strong>Academic Explanation:</strong><br><br>
                      Business is a prominent commercial activity characterized by the following features:<br><br>
                      1. <strong>Economic Activity:</strong> It is conducted with the main purpose of earning profit and livelihood, not out of love or affection.<br>
                      2. <strong>Exchange of Goods and Services:</strong> Every business transaction involves the transfer or sale of goods and services for value.<br>
                      3. <strong>Regularity in Dealings:</strong> A single transaction does not constitute a business. There must be regular and continuous transactions of purchase and sale.<br>
                      4. <strong>Profit Motive:</strong> Profit earning is the primary force that drives business activities.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `history-civics`,
      subjectName: `History & Civics`,
      books: [
        {
          id: `total-history-civics-9`,
          name: `Total History & Civics Class 9 (Morning Star)`,
          chapters: [
            {
              id: `harappan-civilization-9`,
              name: `Chapter 1: The Harappan Civilization`,
              exercises: [
                {
                  id: `review-questions-9`,
                  name: `Review Questions`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Mention two important sources of information about the Harappan Civilization.`,
                      solution: `<strong>Academic Explanation:</strong><br><br>
                      The two primary sources that provide information about the ancient Harappan Civilization are:<br><br>
                      1. <strong>The Great Bath of Mohenjo-daro:</strong> This large public pool structure reveals the advanced architectural, civil engineering, and hydrologic skills of the Harappan people. It indicates the importance of ceremonial public bathing and public sanitation in their culture.<br>
                      2. <strong>The Harappan Seals:</strong> Over 2,000 clay and steatite seals discovered across Harappan sites show pictures of animals, physical figures, and scripts. They provide clues about their religious beliefs, commercial trade networks, and artistic skills.`
                    },
                    {
                      qNo: `2`,
                      questionText: `State two prominent features of the town planning of the Harappan Civilization.`,
                      solution: `<strong>Academic Explanation:</strong><br><br>
                      Two prominent features of Harappan town planning are:<br><br>
                      1. <strong>Grid Iron Pattern Streets:</strong> The streets of Harappan cities (like Harappa and Mohenjo-daro) were laid out in an orderly grid system, running north to south and east to west, cutting each other at right angles.<br>
                      2. <strong>Advanced Drainage System:</strong> Every house was connected to a street drain. These drains were constructed using bricks or stone slabs and featured loose covers that could be easily removed for cleaning, indicating a high standard of public hygiene.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    }
  ],
  pastPapers: [
    {
      year: 2025,
      subject: `Mathematics`,
      title: `ICSE 2025 Class 9 Mathematics Annual Examination`,
      questionPaperDownloadUrl: `https://example.com/downloads/icse-2025-maths-9.pdf`,
      markingSchemeSolutions: [
        {
          number: `Question 1`,
          section: `Section A (Compulsory)`,
          text: `Find the value of $x$ if $3^{x-1} = 27$.`,
          solution: `<strong>Marking-Scheme Solution:</strong><br><br>
          Given equation:<br>
          $$3^{x-1} = 27$$<br>
          Express 27 as a base of 3:<br>
          $$27 = 3^3$$<br>
          Substitute back into the equation:<br>
          $$3^{x-1} = 3^3$$<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span><br><br>
          Since the bases are identical, equate the exponents:<br>
          $$x - 1 = 3$$<br>
          $$x = 3 + 1 = 4$$<br>
          The value of $x$ is <strong>4</strong>.<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span>`
        }
      ]
    },
    {
      year: 2024,
      subject: `Physics`,
      title: `ICSE 2024 Class 9 Physics Annual Examination`,
      questionPaperDownloadUrl: `https://example.com/downloads/icse-2024-physics-9.pdf`,
      markingSchemeSolutions: [
        {
          number: `Question 1`,
          section: `Section A (Compulsory)`,
          text: `A measuring cylinder has 50 division marks between 10 mL and 20 mL. Calculate its least count.`,
          solution: `<strong>Marking-Scheme Solution:</strong><br><br>
          <strong>Step 1: Calculate the value of the main range.</strong><br>
          Difference in volume = $20\\text{ mL} - 10\\text{ mL} = 10\\text{ mL}$<br>
          Total number of divisions within this range = 50<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span><br><br>
          
          <strong>Step 2: Apply the Least Count formula.</strong><br>
          $$\\text{Least Count} = \\frac{\\text{Value of Range}}{\\text{Total Divisions}}$$<br>
          $$\\text{Least Count} = \\frac{10\\text{ mL}}{50} = 0.2\\text{ mL}$$<br>
          The least count of the cylinder is <strong>0.2 mL</strong>.<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span>`
        }
      ]
    }
  ]
};

const Class11Data = {
  className: `ISC Class 11`,
  board: `ISC`,
  classLevel: 11,
  textbookAnswers: [
    {
      subjectId: `mathematics`,
      subjectName: `Mathematics`,
      books: [
        {
          id: `op-malhotra-maths-11`,
          name: `S. Chand's ISC Mathematics Class XI (O.P. Malhotra)`,
          chapters: [
            {
              id: `sets-11`,
              name: `Chapter 1: Sets`,
              exercises: [
                {
                  id: `ex-1-1`,
                  name: `Exercise 1.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `If $A = \\{1, 2, 3\\}$, find all its subsets and calculate the power set $P(A)$ cardinality.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Write down all possible subsets of set A.</strong><br>
                      A subset can have 0, 1, 2, or 3 elements:<br>
                      - Subset with 0 elements (Null set): $\\emptyset$ (or $\\{\\}$)<br>
                      - Subsets with 1 element: $\\{1\\}$, $\\{2\\}$, $\\{3\\}$<br>
                      - Subsets with 2 elements: $\\{1, 2\\}$, $\\{1, 3\\}$, $\\{2, 3\\}$<br>
                      - Subset with 3 elements: $\\{1, 2, 3\\}$<br><br>
                      The list of all subsets is:<br>
                      <strong>\\{\\emptyset, \\{1\\}, \\{2\\}, \\{3\\}, \\{1, 2\\}, \\{1, 3\\}, \\{2, 3\\}, \\{1, 2, 3\\}\\}</strong>.<br><br>
                      
                      <strong>Step 2: Calculate power set cardinality using the subset formula.</strong><br>
                      The power set $P(A)$ is the set of all subsets of $A$. If a set $A$ has $n$ elements, the number of elements in the power set is:<br>
                      $$n(P(A)) = 2^n$$<br>
                      Given $n = 3$:<br>
                      $$n(P(A)) = 2^3 = 8$$<br>
                      The cardinality of the power set $P(A)$ is <strong>8</strong>.`
                    },
                    {
                      qNo: `2`,
                      questionText: `Prove De Morgan's First Law $(A \\cup B)' = A' \\cap B'$ algebraically using logical membership.`,
                      solution: `<strong>Step-by-Step Algebraic Proof:</strong><br><br>
                      To prove $(A \\cup B)' = A' \\cap B'$, we must show two-way containment:<br>
                      1. $(A \\cup B)' \\subseteq A' \\cap B'$<br>
                      2. $A' \\cap B' \\subseteq (A \\cup B)'$<br><br>
                      
                      <strong>Part 1: Prove $(A \\cup B)' \\subseteq A' \\cap B'$</strong><br>
                      Let $x$ be an arbitrary element of $(A \\cup B)'$:<br>
                      $$x \\in (A \\cup B)' \\implies x \\notin (A \\cup B)$$<br>
                      $$x \\notin (A \\cup B) \\implies x \\notin A \\text{ and } x \\notin B$$<br>
                      $$x \\notin A \\implies x \\in A'$$<br>
                      $$x \\notin B \\implies x \\in B'$$<br>
                      $$x \\in A' \\text{ and } x \\in B' \\implies x \\in (A' \\cap B')$$<br>
                      Since $x \\in (A \\cup B)' \\implies x \\in (A' \\cap B')$, we conclude:<br>
                      $$(A \\cup B)' \\subseteq A' \\cap B' \\quad \\text{--- (Condition 1)}$$<br><br>
                      
                      <strong>Part 2: Prove $A' \\cap B' \\subseteq (A \\cup B)'$</strong><br>
                      Let $y$ be an arbitrary element of $A' \\cap B'$:<br>
                      $$y \\in A' \\cap B' \\implies y \\in A' \\text{ and } y \\in B'$$<br>
                      $$y \\in A' \\implies y \\notin A$$<br>
                      $$y \\in B' \\implies y \\notin B$$<br>
                      $$y \\notin A \\text{ and } y \\notin B \\implies y \\notin (A \\cup B)$$<br>
                      $$y \\notin (A \\cup B) \\implies y \\in (A \\cup B)'$$<br>
                      Since $y \\in A' \\cap B' \\implies y \\in (A \\cup B)'$, we conclude:<br>
                      $$A' \\cap B' \\subseteq (A \\cup B)' \\quad \\text{--- (Condition 2)}$$<br><br>
                      
                      <strong>Conclusion:</strong><br>
                      From Condition 1 and Condition 2, both sets contain each other. Hence:<br>
                      $$(A \\cup B)' = A' \\cap B'$$`
                    }
                  ]
                }
              ]
            },
            {
              id: `relations-functions-11`,
              name: `Chapter 2: Relations & Functions`,
              exercises: [
                {
                  id: `ex-2-1`,
                  name: `Exercise 2.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `If $A = \\{1, 2\\}$ and $B = \\{3, 4, 5\\}$, find the Cartesian Product $A \\times B$ and state its cardinality.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Calculate the Cartesian Product.</strong><br>
                      The Cartesian Product $A \\times B$ is the set of all ordered pairs $(a, b)$ where $a \\in A$ and $b \\in B$:<br>
                      $$A \\times B = \\{(1, 3), (1, 4), (1, 5), (2, 3), (2, 4), (2, 5)\\}$$<br><br>
                      
                      <strong>Step 2: Find the cardinality of the product.</strong><br>
                      The cardinality formula is:<br>
                      $$n(A \\times B) = n(A) \\times n(B)$$<br>
                      Given $n(A) = 2$ and $n(B) = 3$:<br>
                      $$n(A \\times B) = 2 \\times 3 = 6$$<br>
                      The Cartesian Product has <strong>6</strong> ordered pairs.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `physics`,
      subjectName: `Physics`,
      books: [
        {
          id: `nootan-physics-11`,
          name: `Nootan Physics (Kumar-Mittal)`,
          chapters: [
            {
              id: `dimensional-analysis-11-senior`,
              name: `Chapter 1: Dimensional Analysis`,
              exercises: [
                {
                  id: `ex-1-1-phy`,
                  name: `Exercise 1.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Determine the dimensional formula of the Universal Gravitational Constant ($G$).`,
                      solution: `<strong>Step-by-Step Derivation:</strong><br><br>
                      <strong>Step 1: Write down the governing physical equation.</strong><br>
                      According to Newton's Law of Universal Gravitation:<br>
                      $$F = G \\frac{m_1 m_2}{r^2}$$<br>
                      Rearranging to isolate $G$:<br>
                      $$G = \\frac{F \\cdot r^2}{m_1 m_2}$$<br><br>
                      
                      <strong>Step 2: State the dimensions of each variable.</strong><br>
                      - Force ($F$) = $[M L T^{-2}]$<br>
                      - Distance squared ($r^2$) = $[L^2]$<br>
                      - Product of masses ($m_1 m_2$) = $[M \\cdot M] = [M^2]$<br><br>
                      
                      <strong>Step 3: Solve the combined dimensional ratios.</strong><br>
                      $$[G] = \\frac{[M L T^{-2}] [L^2]}{[M^2]}$$<br>
                      $$[G] = [M^{1 - 2} L^{1 + 2} T^{-2}]$$<br>
                      $$[G] = [M^{-1} L^3 T^{-2}]$$<br>
                      The dimensional formula of $G$ is <strong>$[M^{-1} L^3 T^{-2}]$</strong>.`
                    },
                    {
                      qNo: `2`,
                      questionText: `Verify the dimensional correctness of the time period equation of a simple pendulum: $T = 2\\pi \\sqrt{\\frac{l}{g}}$.`,
                      solution: `<strong>Step-by-Step Verification:</strong><br><br>
                      According to the principle of homogeneity, dimensions of both sides must match.<br><br>
                      
                      <strong>Step 1: Dimension of LHS.</strong><br>
                      LHS represents Time Period ($T$):<br>
                      $$\\text{LHS} = [T] = [M^0 L^0 T^1]$$<br><br>
                      
                      <strong>Step 2: Dimension of RHS.</strong><br>
                      The constant $2\\pi$ is a dimensionless factor and is omitted in dimensional equations.<br>
                      $$\\text{RHS} = \\left[ \\sqrt{\\frac{l}{g}} \\right] = \\left[ \\frac{l}{g} \\right]^{1/2}$$<br>
                      Substitute the dimensions of Length ($[L]$) and acceleration due to gravity ($[L T^{-2}]$):<br>
                      $$\\text{RHS} = \\left[ \\frac{L}{L T^{-2}} \\right]^{1/2} = [T^2]^{1/2} = [T]$$<br>
                      $$\\text{RHS} = [M^0 L^0 T^1]$$<br><br>
                      
                      <strong>Conclusion:</strong><br>
                      LHS dimensions match RHS dimensions ($[T]$). The equation is <strong>dimensionally correct</strong>.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `chemistry`,
      subjectName: `Chemistry`,
      books: [
        {
          id: `nootan-chemistry-11`,
          name: `Nootan Chemistry (Dr. H.C. Srivastava)`,
          chapters: [
            {
              id: `basic-concepts-11-senior`,
              name: `Chapter 1: Some Basic Concepts of Chemistry`,
              exercises: [
                {
                  id: `ex-1-1-chem`,
                  name: `Exercise 1.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `An organic compound contains 40.0% Carbon, 6.67% Hydrogen, and 53.33% Oxygen by mass. If its vapor density is 30, determine its empirical and molecular formulas.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Determine the element relative atomic ratio.</strong><br>
                      <table border="1" cellpadding="5" style="border-collapse: collapse; width:100%; text-align:left; border: 1px solid #ccc;">
                        <thead>
                          <tr style="background-color: #f1f5f9;">
                            <th>Element</th>
                            <th>Mass %</th>
                            <th>Atomic Mass</th>
                            <th>Atomic Ratio</th>
                            <th>Simplest Ratio</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Carbon (C)</strong></td>
                            <td>40.0</td>
                            <td>12</td>
                            <td>40.0 / 12 = 3.33</td>
                            <td>3.33 / 3.33 = 1</td>
                          </tr>
                          <tr>
                            <td><strong>Hydrogen (H)</strong></td>
                            <td>6.67</td>
                            <td>1</td>
                            <td>6.67 / 1 = 6.67</td>
                            <td>6.67 / 3.33 = 2</td>
                          </tr>
                          <tr>
                            <td><strong>Oxygen (O)</strong></td>
                            <td>53.33</td>
                            <td>16</td>
                            <td>53.33 / 16 = 3.33</td>
                            <td>3.33 / 3.33 = 1</td>
                          </tr>
                        </tbody>
                      </table><br>
                      The <strong>Empirical Formula</strong> is <strong>$\\text{CH}_2\\text{O}$</strong>.<br><br>
                      
                      <strong>Step 2: Calculate the Empirical Formula Mass.</strong><br>
                      $$\\text{Empirical Mass} = 12(C) + 2\\times 1(H) + 16(O) = 30\\text{ g/mol}$$<br>
                      
                      <strong>Step 3: Calculate Molecular Mass.</strong><br>
                      $$\\text{Molecular Mass} = 2 \\times \\text{Vapor Density} = 2 \\times 30 = 60\\text{ g/mol}$$<br>
                      
                      <strong>Step 4: Find molecular multiplier value 'n'.</strong><br>
                      $$n = \\frac{\\text{Molecular Mass}}{\\text{Empirical Mass}} = \\frac{60}{30} = 2$$<br>
                      $$\\text{Molecular Formula} = n \\times (\\text{Empirical Formula}) = 2 \\times (\\text{CH}_2\\text{O}) = \\text{C}_2\\text{H}_4\\text{O}_2$$<br>
                      The empirical formula is <strong>$\\text{CH}_2\\text{O}$</strong>, and the molecular formula is <strong>$\\text{C}_2\\text{H}_4\\text{O}_2$</strong>.`
                    },
                    {
                      qNo: `2`,
                      questionText: `Calculate the molarity of a solution containing 4.0 g of NaOH dissolved in 250 mL of water.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Calculate the molar mass of Sodium Hydroxide (NaOH).</strong><br>
                      $$\\text{Molar Mass of NaOH} = \\text{Na}(23) + \\text{O}(16) + \\text{H}(1) = 40\\text{ g/mol}$$<br><br>
                      
                      <strong>Step 2: Calculate number of moles of NaOH.</strong><br>
                      $$\\text{Moles of NaOH} = \\frac{\\text{Mass}}{\\text{Molar Mass}} = \\frac{4.0\\text{ g}}{40\\text{ g/mol}} = 0.1\\text{ moles}$$<br><br>
                      
                      <strong>Step 3: Convert the volume of water from mL into Liters.</strong><br>
                      $$V = 250\\text{ mL} = \\frac{250}{1000}\\text{ L} = 0.25\\text{ L}$$<br><br>
                      
                      <strong>Step 4: Calculate Molarity (M).</strong><br>
                      $$\\text{Molarity} = \\frac{\\text{Moles of Solute}}{\\text{Volume of Solution in Liters}}$$<br>
                      $$\\text{Molarity} = \\frac{0.1}{0.25} = 0.4\\text{ M}$$<br>
                      The molarity of the solution is <strong>0.4 M</strong>.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `accountancy`,
      subjectName: `Accountancy`,
      books: [
        {
          id: `ts-grewal-accounts-11`,
          name: `T.S. Grewal Accountancy Class XI`,
          chapters: [
            {
              id: `accounting-equation-11-senior`,
              name: `Chapter 1: Accounting Equation`,
              exercises: [
                {
                  id: `ex-1-1-acc`,
                  name: `Exercise 1.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `State the fundamental Accounting Equation. Calculate Capital if Assets = ₹5,00,000 and Liabilities = ₹1,50,000.`,
                      solution: `<strong>Step-by-Step Accounting Solution:</strong><br><br>
                      The dual-entry structural framework is defined by the basic accounting equation:<br>
                      $$\\text{Assets} = \\text{Liabilities} + \\text{Capital}$$<br><br>
                      
                      Rearranging to solve for Capital:<br>
                      $$\\text{Capital} = \\text{Assets} - \\text{Liabilities}$$<br>
                      Substitute the given financial parameters:<br>
                      $$\\text{Capital} = 5,00,000 - 1,50,000 = ₹3,50,000$$<br>
                      The value of Capital is <strong>₹3,50,000</strong>.`
                    },
                    {
                      qNo: `2`,
                      questionText: `Show the transaction effect on the Accounting Equation:<br>(a) Commenced business with Cash ₹2,00,000.<br>(b) Purchased goods for cash ₹50,000.`,
                      solution: `<strong>Accounting Analysis and Statement:</strong><br><br>
                      We trace the dual impact of each transaction systematically:<br><br>
                      
                      <strong>Transaction (a): Commenced business with Cash ₹2,00,000.</strong><br>
                      - Assets (Cash) increases by ₹2,00,000.<br>
                      - Capital increases by ₹2,00,000.<br>
                      $$\\text{Assets (Cash: 2,00,000)} = \\text{Liabilities (0)} + \\text{Capital (2,00,000)}$$<br><br>
                      
                      <strong>Transaction (b): Purchased goods for cash ₹50,000.</strong><br>
                      - Assets (Cash) decreases by ₹50,000.<br>
                      - Assets (Stock) increases by ₹50,000.<br>
                      - Capital remains unchanged.<br>
                      $$\\text{Assets (Cash: 1,50,000 + Stock: 50,000)} = \\text{Liabilities (0)} + \\text{Capital (2,00,000)}$$<br><br>
                      
                      <strong>Final Position Summary:</strong><br>
                      $$\\text{Total Assets (₹2,00,000)} = \\text{Total Liabilities & Capital (₹2,00,000)}$$`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `economics`,
      subjectName: `Economics`,
      books: [
        {
          id: `frank-economics-11`,
          name: `Frank ISC Economics`,
          chapters: [
            {
              id: `definitions-economics-11`,
              name: `Chapter 1: Definition of Economics`,
              exercises: [
                {
                  id: `ex-1-1-eco`,
                  name: `Exercise 1.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Differentiate between Adam Smith's wealth definition and Alfred Marshall's welfare definition of economics.`,
                      solution: `<strong>Academic Comparative Analysis:</strong><br><br>
                      <table border="1" cellpadding="5" style="border-collapse: collapse; width:100%; text-align:left; border: 1px solid #ccc;">
                        <thead>
                          <tr style="background-color: #f1f5f9;">
                            <th>Parameter</th>
                            <th>Adam Smith (Wealth Definition)</th>
                            <th>Alfred Marshall (Welfare Definition)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Core Focus</strong></td>
                            <td>An inquiry into the nature and causes of the wealth of nations. It treats wealth as an end in itself.</td>
                            <td>The study of mankind in the ordinary business of life; focuses on human material welfare.</td>
                          </tr>
                          <tr>
                            <td><strong>Scope</strong></td>
                            <td>Focuses primarily on 'Economic Man' who is driven solely by self-interest and accumulation of material wealth.</td>
                            <td>Focuses on 'Real Man' in society, emphasizing material activities that enhance human well-being.</td>
                          </tr>
                          <tr>
                            <td><strong>Subject Matter</strong></td>
                            <td>Excludes immaterial services, focusing strictly on production and consumption of tangible goods.</td>
                            <td>Emphasizes that wealth is merely a means to achieve the ultimate goal: human welfare.</td>
                          </tr>
                        </tbody>
                      </table>`
                    },
                    {
                      qNo: `2`,
                      questionText: `Explain the scarcity-oriented definition of economics formulated by Lionel Robbins.`,
                      solution: `<strong>Academic Explanation:</strong><br><br>
                      Lionel Robbins defined economics as:<br>
                      <i>'Economics is the science which studies human behavior as a relationship between ends and scarce means which have alternative uses.'</i><br><br>
                      
                      <strong>Four Pillars of Robbins' Scarcity Definition:</strong><br><br>
                      1. <strong>Unlimited Ends:</strong> Human wants are infinite and recurring. As soon as one want is satisfied, another arises.<br>
                      2. <strong>Scarce Means:</strong> The resources (time, money, materials) available to satisfy these infinite wants are limited or scarce in relation to their demand.<br>
                      3. <strong>Alternative Uses of Means:</strong> Scarce resources can be put to alternative uses (e.g., land can be used to grow wheat, construct a school, or build a factory).<br>
                      4. <strong>The Problem of Choice:</strong> Since wants are unlimited and resources are scarce and have alternative uses, a choice must be made, which forms the fundamental economic problem.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    }
  ],
  pastPapers: [
    {
      year: 2025,
      subject: `Mathematics`,
      title: `ISC 2025 Class 11 Mathematics Annual Examination`,
      questionPaperDownloadUrl: `https://example.com/downloads/isc-2025-maths-11.pdf`,
      markingSchemeSolutions: [
        {
          number: `Question 1`,
          section: `Section A`,
          text: `Solve for $x$: $3^{2x+1} - 10 \\cdot 3^x + 3 = 0$.`,
          solution: `<strong>Marking-Scheme Solution:</strong><br><br>
          Given equation:<br>
          $$3^{2x+1} - 10 \\cdot 3^x + 3 = 0$$<br>
          Express the first term using exponential rules:<br>
          $$3 \\cdot (3^x)^2 - 10 \\cdot 3^x + 3 = 0$$<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span><br><br>
          
          Let $y = 3^x$. Substitute this into the equation:<br>
          $$3y^2 - 10y + 3 = 0$$<br>
          Factor the quadratic by splitting the middle term:<br>
          $$3y^2 - 9y - y + 3 = 0$$<br>
          $$3y(y - 3) - 1(y - 3) = 0$$<br>
          $$(3y - 1)(y - 3) = 0 \\implies y = \\frac{1}{3} \\text{ or } y = 3$$<br>
          <span class="marking-scheme-badge">[Allocated Marks: 2]</span><br><br>
          
          Now, solve for $x$ by substituting back $y = 3^x$:<br>
          - Case 1: $3^x = 3^1 \\implies x = 1$<br>
          - Case 2: $3^x = \\frac{1}{3} = 3^{-1} \\implies x = -1$<br>
          The solutions for $x$ are <strong>$1$ and $-1$</strong>.<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span>`
        }
      ]
    },
    {
      year: 2024,
      subject: `Physics`,
      title: `ISC 2024 Class 11 Physics Annual Examination`,
      questionPaperDownloadUrl: `https://example.com/downloads/isc-2024-physics-11.pdf`,
      markingSchemeSolutions: [
        {
          number: `Question 1`,
          section: `Section A`,
          text: `Prove that the trajectory of a projectile launched at an angle $\\theta$ with the horizontal is parabolic in nature.`,
          solution: `<strong>Marking-Scheme Solution:</strong><br><br>
          <strong>Step 1: Set up the components of motion.</strong><br>
          Let a projectile be launched with an initial velocity $u$ at an angle $\\theta$ to the horizontal.<br>
          - Horizontal component of velocity: $u_x = u \\cos\\theta$<br>
          - Vertical component of velocity: $u_y = u \\sin\\theta$<br><br>
          
          <strong>Step 2: Derive horizontal position as a function of time.</strong><br>
          Since horizontal acceleration is zero ($a_x = 0$):<br>
          $$x = u_x \\cdot t = (u \\cos\\theta) \\cdot t \\implies t = \\frac{x}{u \\cos\\theta} \\quad \\text{--- (Equation 1)}$$<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span><br><br>
          
          <strong>Step 3: Derive vertical position and substitute t.</strong><br>
          Vertical acceleration is acting downwards due to gravity ($a_y = -g$):<br>
          $$y = u_y \\cdot t - \\frac{1}{2}gt^2$$<br>
          $$y = (u \\sin\\theta) \\cdot t - \\frac{1}{2}gt^2$$<br>
          Substitute the expression for $t$ from Equation 1:<br>
          $$y = (u \\sin\\theta) \\left( \\frac{x}{u \\cos\\theta} \\right) - \\frac{1}{2}g \\left( \\frac{x}{u \\cos\\theta} \\right)^2$$<br>
          $$y = x \\tan\\theta - \\frac{gx^2}{2u^2 \\cos^2\\theta}$$<br>
          <span class="marking-scheme-badge">[Allocated Marks: 2]</span><br><br>
          
          <strong>Conclusion:</strong><br>
          The derived equation is of the form $y = ax - bx^2$, which represents a second-degree polynomial equation. This confirms that the trajectory of a projectile is a <strong>parabola</strong>.<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span>`
        }
      ]
    }
  ]
};

const Class12Data = {
  className: `ISC Class 12`,
  board: `ISC`,
  classLevel: 12,
  textbookAnswers: [
    {
      subjectId: `mathematics`,
      subjectName: `Mathematics`,
      books: [
        {
          id: `op-malhotra-maths-12`,
          name: `S. Chand's ISC Mathematics Vol 2 (O.P. Malhotra)`,
          chapters: [
            {
              id: `relations-functions-12`,
              name: `Chapter 1: Relations and Functions`,
              exercises: [
                {
                  id: `ex-1-1`,
                  name: `Exercise 1.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Prove that the function $f: \\mathbb{R} \\rightarrow \\mathbb{R}$ defined by $f(x) = 3x - 5$ is a bijection.`,
                      solution: `<strong>Step-by-Step Proof:</strong><br><br>
                      To prove that $f(x)$ is a bijection, we must show that it is both <strong>One-to-One (Injective)</strong> and <strong>Onto (Surjective)</strong>.<br><br>
                      
                      <strong>Step 1: Injectivity (One-to-One)</strong><br>
                      Let $x_1, x_2 \\in \\mathbb{R}$ such that $f(x_1) = f(x_2)$:<br>
                      $$3x_1 - 5 = 3x_2 - 5$$<br>
                      Add 5 to both sides:<br>
                      $$3x_1 = 3x_2$$<br>
                      Divide by 3:<br>
                      $$x_1 = x_2$$<br>
                      Since $f(x_1) = f(x_2) \\implies x_1 = x_2$, the function is <strong>One-to-One</strong>.<br><br>
                      
                      <strong>Step 2: Surjectivity (Onto)</strong><br>
                      Let $y \\in \\mathbb{R}$ (codomain). Set $y = f(x)$ and solve for $x$ in terms of $y$:<br>
                      $$y = 3x - 5$$<br>
                      $$y + 5 = 3x \\implies x = \\frac{y + 5}{3}$$<br>
                      Since $y \\in \\mathbb{R}$ (codomain), $x = \\frac{y+5}{3}$ is also a real number ($\\in \\mathbb{R}$), meaning an input pre-image exists in the domain for every output. Let's verify $f(x) = y$:<br>
                      $$f\\left(\\frac{y + 5}{3}\\right) = 3\\left(\\frac{y + 5}{3}\\right) - 5 = y + 5 - 5 = y$$<br>
                      Since the range equals the codomain, the function is <strong>Onto</strong>.<br><br>
                      
                      <strong>Conclusion:</strong><br>
                      Since the function is both One-to-One and Onto, it is a <strong>bijection (bijective function)</strong>.`
                    },
                    {
                      qNo: `2`,
                      questionText: `If $f(x) = \\frac{x-2}{x-3}$ where $x \\neq 3$, find the inverse function $f^{-1}(x)$.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Set $y = f(x)$ and rearrange.</strong><br>
                      $$y = \\frac{x-2}{x-3}$$<br>
                      Multiply both sides by $(x-3)$:<br>
                      $$y(x-3) = x-2$$<br>
                      $$xy - 3y = x-2$$<br><br>
                      
                      <strong>Step 2: Isolate the variable $x$.</strong><br>
                      Bring all terms involving $x$ to LHS and other terms to RHS:<br>
                      $$xy - x = 3y - 2$$<br>
                      Factor out $x$ on LHS:<br>
                      $$x(y - 1) = 3y - 2$$<br>
                      $$x = \\frac{3y - 2}{y - 1}$$<br><br>
                      
                      <strong>Step 3: Define the inverse function.</strong><br>
                      Since $x = f^{-1}(y)$:<br>
                      $$f^{-1}(y) = \\frac{3y-2}{y-1}$$<br>
                      Replacing the variable $y$ with $x$:<br>
                      $$f^{-1}(x) = \\frac{3x-2}{x-1} \\quad (x \\neq 1)$$<br>
                      The inverse function is <strong>$f^{-1}(x) = \\frac{3x-2}{x-1}$</strong>.`
                    }
                  ]
                }
              ]
            },
            {
              id: `calculus-integrals-12`,
              name: `Chapter 8: Calculus Integrals`,
              exercises: [
                {
                  id: `ex-8-1`,
                  name: `Exercise 8.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Evaluate the integral using integration by parts: $\\int x^2 e^x \\, dx$.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      According to the integration by parts formula:<br>
                      $$\\int u \\, dv = u v - \\int v \\, du$$<br>
                      We choose $u$ and $dv$ using the ILATE rule.<br><br>
                      
                      <strong>Step 1: Apply parts for the first iteration.</strong><br>
                      Let $u = x^2 \\implies du = 2x \\, dx$<br>
                      Let $dv = e^x \\, dx \\implies v = e^x$<br>
                      Applying the parts formula:<br>
                      $$\\int x^2 e^x \\, dx = x^2 e^x - \\int e^x (2x) \\, dx$$<br>
                      $$\\int x^2 e^x \\, dx = x^2 e^x - 2 \\int x e^x \\, dx \\quad \\text{--- (Equation 1)}$$<br><br>
                      
                      <strong>Step 2: Apply parts for the second iteration on $\\int x e^x \\, dx$.</strong><br>
                      Let $u_1 = x \\implies du_1 = dx$<br>
                      Let $dv_1 = e^x \\, dx \\implies v_1 = e^x$<br>
                      Applying parts formula:<br>
                      $$\\int x e^x \\, dx = x e^x - \\int e^x \\, dx = x e^x - e^x$$<br><br>
                      
                      <strong>Step 3: Substitute back into Equation 1.</strong><br>
                      $$\\int x^2 e^x \\, dx = x^2 e^x - 2(x e^x - e^x) + C$$<br>
                      $$\\int x^2 e^x \\, dx = e^x (x^2 - 2x + 2) + C$$<br>
                      The value of the integral is <strong>$e^x (x^2 - 2x + 2) + C$</strong>.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `physics`,
      subjectName: `Physics`,
      books: [
        {
          id: `nootan-physics-12`,
          name: `Nootan Class 12 Physics (Kumar & Mittal)`,
          chapters: [
            {
              id: `electrostatics-12-senior`,
              name: `Chapter 1: Electrostatics`,
              exercises: [
                {
                  id: `ex-1-1-phy`,
                  name: `Exercise 1.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `State Gauss's Theorem and derive an expression for the electric field intensity due to an infinitely long straight charged wire.`,
                      solution: `<strong>Step-by-Step Explanation & Derivation:</strong><br><br>
                      <strong>Gauss's Theorem Statement:</strong><br>
                      The total electric flux passing through any closed surface is equal to $\\frac{1}{\\varepsilon_0}$ times the net charge enclosed by the surface:<br>
                      $$\\oint_S \\vec{E} \\cdot d\\vec{A} = \\frac{q}{\\varepsilon_0}$$<br><br>
                      
                      <strong>Derivation for an Infinitely Long Charged Wire:</strong><br>
                      Let $\\lambda$ be the linear charge density (charge per unit length) of the wire.<br><br>
                      
                      <strong>Step 1: Construct a Gaussian surface.</strong><br>
                      Choose a coaxial cylindrical Gaussian surface of radius $r$ and length $l$ around the wire. The surface has three parts:<br>
                      - Curved surface $S_1$ where $\\vec{E}$ and the normal vector $d\\vec{A}$ are parallel ($0^\\circ$).<br>
                      - Flat circular end caps $S_2$ and $S_3$ where $\\vec{E}$ and $d\\vec{A}$ are perpendicular ($90^\\circ$).<br><br>
                      
                      <strong>Step 2: Calculate the net electric flux.</strong><br>
                      $$\\Phi = \\oint_{S_1} E \\, dA \\cos 0^\\circ + \\oint_{S_2} E \\, dA \\cos 90^\\circ + \\oint_{S_3} E \\, dA \\cos 90^\\circ$$<br>
                      $$\\Phi = E \\oint_{S_1} dA = E (2\\pi r l)$$<br><br>
                      
                      <strong>Step 3: Apply Gauss's Theorem.</strong><br>
                      The charge enclosed by the cylinder is $q = \\lambda l$.<br>
                      $$\\Phi = \\frac{q}{\\varepsilon_0} \\implies E (2\\pi r l) = \\frac{\\lambda l}{\\varepsilon_0}$$<br>
                      $$E = \\frac{\\lambda}{2\\pi \\varepsilon_0 r}$$<br>
                      The electric field intensity is <strong>$E = \\frac{\\lambda}{2\\pi \\varepsilon_0 r}$</strong>.`
                    },
                    {
                      qNo: `2`,
                      questionText: `Three capacitors of capacities $2 \\mu\\text{F}$, $3 \\mu\\text{F}$, and $6 \\mu\\text{F}$ are connected in series across a 100 V source. Find the equivalent capacity and the charge on each capacitor.`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Calculate the equivalent capacity ($C_s$).</strong><br>
                      For capacitors connected in series, the reciprocal of equivalent capacity is the sum of reciprocals of individual capacities:<br>
                      $$\\frac{1}{C_s} = \\frac{1}{C_1} + \\frac{1}{C_2} + \\frac{1}{C_3}$$<br>
                      $$\\frac{1}{C_s} = \\frac{1}{2} + \\frac{1}{3} + \\frac{1}{6}$$<br>
                      Find the common denominator (6):<br>
                      $$\\frac{1}{C_s} = \\frac{3 + 2 + 1}{6} = \\frac{6}{6} = 1 \\implies C_s = 1 \\mu\\text{F}$$<br><br>
                      
                      <strong>Step 2: Calculate the charge on each capacitor.</strong><br>
                      In a series connection, the charge ($Q$) on each capacitor is identical:<br>
                      $$Q = C_s \\times V$$<br>
                      $$Q = 1 \\mu\\text{F} \\times 100 \\text{ V} = 100 \\mu\\text{C}$$<br>
                      The equivalent capacity is <strong>$1 \\mu\\text{F}$</strong>, and the charge on each capacitor is <strong>$100 \\mu\\text{C}$</strong>.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `chemistry`,
      subjectName: `Chemistry`,
      books: [
        {
          id: `nootan-chemistry-12`,
          name: `Nootan Chemistry Class 12 (Dr. H.C. Srivastava)`,
          chapters: [
            {
              id: `solutions-12-senior`,
              name: `Chapter 1: Solutions`,
              exercises: [
                {
                  id: `ex-1-1-chem`,
                  name: `Exercise 1.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `State Raoult's Law for a solution containing non-volatile solute. Explain why non-ideal solutions show positive deviations.`,
                      solution: `<strong>Academic Explanation:</strong><br><br>
                      <strong>Raoult's Law:</strong><br>
                      Raoult's Law states that the relative lowering of vapor pressure of a dilute solution containing a non-volatile solute is equal to the mole fraction of the solute in the solution:<br>
                      $$\\frac{p^0 - p}{p^0} = x_{\\text{solute}}$$<br>
                      where $p^0$ is the vapor pressure of pure solvent, $p$ is the vapor pressure of the solution, and $x_{\\text{solute}}$ is the mole fraction of the solute.<br><br>
                      
                      <strong>Positive Deviation from Raoult's Law:</strong><br>
                      A solution exhibits a positive deviation when the cohesive intermolecular forces between solvent-solute molecules ($A-B$ interactions) are weaker than those between solvent-solvent ($A-A$) and solute-solute ($B-B$) molecules.<br><br>
                      Because the $A-B$ molecular bonds are weaker, molecules can escape into the vapor phase more easily. This increases the total vapor pressure of the mixture above the value calculated by Raoult's Law, resulting in an endothermic mixing process ($\\Delta H_{\\text{mix}} > 0$) and volume expansion ($\\Delta V_{\\text{mix}} > 0$).`
                    },
                    {
                      qNo: `2`,
                      questionText: `Calculate the boiling point elevation when 12.5 g of a non-volatile solute is dissolved in 170 g of water. (Given: $K_b = 0.52 \\text{ K kg mol}^{-1}$, and molar mass of solute = 50 g/mol).`,
                      solution: `<strong>Step-by-Step Solution:</strong><br><br>
                      <strong>Step 1: Calculate the molality ($m$) of the solution.</strong><br>
                      $$\\text{Molality (m)} = \\frac{\\text{Moles of Solute}}{\\text{Mass of Solvent in kg}}$$<br>
                      $$\\text{Moles of Solute} = \\frac{\\text{Given Mass}}{\\text{Molar Mass}} = \\frac{12.5 \\text{ g}}{50 \\text{ g/mol}} = 0.25 \\text{ moles}$$<br>
                      $$\\text{Mass of Solvent (Water)} = 170 \\text{ g} = 0.170 \\text{ kg}$$<br>
                      $$\\text{Molality (m)} = \\frac{0.25}{0.170} \\approx 1.47 \\text{ mol/kg}$$<br><br>
                      
                      <strong>Step 2: Apply the boiling point elevation formula.</strong><br>
                      $$\\Delta T_b = K_b \\times m$$<br>
                      $$\\Delta T_b = 0.52 \\times 1.47 \\approx 0.764 \\text{ K}$$<br>
                      The boiling point elevation is approximately <strong>0.764 K</strong>.`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `accountancy`,
      subjectName: `Accountancy`,
      books: [
        {
          id: `ts-grewal-accounts-12`,
          name: `T.S. Grewal ISC Accountancy Class 12`,
          chapters: [
            {
              id: `partnership-accounts-12-senior`,
              name: `Chapter 1: Partnership Accounts`,
              exercises: [
                {
                  id: `ex-1-1-acc`,
                  name: `Exercise 1.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Explain the purpose of preparing a Profit and Loss Appropriation Account. Distinguish between Fixed Capital and Fluctuating Capital accounts of partners.`,
                      solution: `<strong>Step-by-Step Accounting Explanation:</strong><br><br>
                      <strong>P&L Appropriation Account Purpose:</strong><br>
                      The Profit and Loss Appropriation Account is prepared as an extension of the Profit and Loss Account. Its primary purpose is to show how the net profit of the partnership is distributed or allocated among the partners according to the partnership deed (e.g., interest on capital, partner salaries, commission, transfer to reserves, and distribution of remaining profits).<br><br>
                      
                      <strong>Key Differences between Fixed and Fluctuating Capital Accounts:</strong><br><br>
                      <table border="1" cellpadding="5" style="border-collapse: collapse; width:100%; text-align:left; border: 1px solid #ccc;">
                        <thead>
                          <tr style="background-color: #f1f5f9;">
                            <th>Feature</th>
                            <th>Fixed Capital Account</th>
                            <th>Fluctuating Capital Account</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Number of Accounts</strong></td>
                            <td>Two accounts are maintained for each partner: Capital Account and Current Account.</td>
                            <td>Only one account is maintained for each partner: Capital Account.</td>
                          </tr>
                          <tr>
                            <td><strong>Adjustments</strong></td>
                            <td>All adjustments for interest, salary, drawings, and profits are made in the Current Account.</td>
                            <td>All adjustments are made directly in the Capital Account.</td>
                          </tr>
                          <tr>
                            <td><strong>Balance Consistency</strong></td>
                            <td>The Capital Account balance normally remains unchanged unless additional capital is introduced or withdrawn permanently.</td>
                            <td>The Capital Account balance changes continuously with each transaction.</td>
                          </tr>
                        </tbody>
                      </table>`
                    },
                    {
                      qNo: `2`,
                      questionText: `A and B are partners sharing profits in the ratio 3:2. A's capital is ₹1,00,000 and B's is ₹60,000. Interest on capital is allowed at 6% p.a. Net profit before adjustments is ₹20,000. Prepare the Profit and Loss Appropriation Account.`,
                      solution: `<strong>Accounting Solution:</strong><br><br>
                      <strong>Step 1: Calculate Interest on Capital.</strong><br>
                      - For Partner A: 6% of ₹1,00,000 = ₹6,000<br>
                      - For Partner B: 6% of ₹60,000 = ₹3,600<br>
                      Total Interest on Capital = ₹6,000 + ₹3,600 = ₹9,600<br><br>
                      
                      <strong>Step 2: Calculate Distributable Profit.</strong><br>
                      Distributable Profit = Net Profit - Interest on Capital<br>
                      $$\\text{Distributable Profit} = 20,000 - 9,600 = ₹10,400$$<br><br>
                      
                      <strong>Step 3: Allocate Distributable Profit in the ratio 3:2.</strong><br>
                      - Share of A: $10,400 \\times \\frac{3}{5} = ₹6,240$<br>
                      - Share of B: $10,400 \\times \\frac{2}{5} = ₹4,160$<br><br>
                      
                      <strong>Profit and Loss Appropriation Account Statement:</strong><br><br>
                      <table border="1" cellpadding="5" style="border-collapse: collapse; width:100%; text-align:left; border: 1px solid #ccc;">
                        <thead>
                          <tr style="background-color: #f1f5f9;">
                            <th>Debit (Dr.)</th>
                            <th>Amount (₹)</th>
                            <th>Credit (Cr.)</th>
                            <th>Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>To Interest on Capital:<br>
                            - A's Capital: 6,000<br>
                            - B's Capital: 3,600</td>
                            <td>9,600</td>
                            <td>By Net Profit b/d</td>
                            <td>20,000</td>
                          </tr>
                          <tr>
                            <td>To Share of Profit transferred to:<br>
                            - A's Capital A/c: 6,240<br>
                            - B's Capital A/c: 4,160</td>
                            <td>10,400</td>
                            <td></td>
                            <td></td>
                          </tr>
                          <tr style="background-color: #f8fafc;">
                            <td><strong>Total</strong></td>
                            <td><strong>20,000</strong></td>
                            <td><strong>Total</strong></td>
                            <td><strong>20,000</strong></td>
                          </tr>
                        </tbody>
                      </table>`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    },
    {
      subjectId: `economics`,
      subjectName: `Economics`,
      books: [
        {
          id: `frank-economics-12`,
          name: `Frank ISC Economics Class 12`,
          chapters: [
            {
              id: `consumer-behavior-12`,
              name: `Chapter 1: Theory of Consumer Behavior`,
              exercises: [
                {
                  id: `ex-1-1-eco`,
                  name: `Exercise 1.1`,
                  questions: [
                    {
                      qNo: `1`,
                      questionText: `Explain the Law of Diminishing Marginal Utility and list three of its key assumptions.`,
                      solution: `<strong>Academic Explanation:</strong><br><br>
                      <strong>Statement of the Law:</strong><br>
                      The Law of Diminishing Marginal Utility (DMU) states that as a consumer consumes more and more units of a specific commodity continuously, the additional utility (marginal utility) derived from each successive unit decreases.<br><br>
                      
                      <strong>Key Assumptions of the Law:</strong><br><br>
                      1. <strong>Rationality:</strong> The consumer is assumed to be rational, aiming to maximize satisfaction from their given income.<br>
                      2. <strong>Continuous Consumption:</strong> Consumption must be continuous without any significant time gap between units.<br>
                      3. <strong>Homogeneous Units:</strong> All units of the consumed commodity must be identical in size, shape, quality, and taste.<br>
                      4. <strong>Cardinal Measurement:</strong> Utility is assumed to be measurable in cardinal numbers or 'utils'.`
                    },
                    {
                      qNo: `2`,
                      questionText: `Differentiate between the Cardinal Utility and Ordinal Utility approaches.`,
                      solution: `<strong>Comparative Economic Solution:</strong><br><br>
                      <table border="1" cellpadding="5" style="border-collapse: collapse; width:100%; text-align:left; border: 1px solid #ccc;">
                        <thead>
                          <tr style="background-color: #f1f5f9;">
                            <th>Feature</th>
                            <th>Cardinal Utility Approach</th>
                            <th>Ordinal Utility Approach</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td><strong>Measurement</strong></td>
                            <td>Assumes utility can be measured quantitatively in absolute terms (e.g., 1, 2, 3 utils).</td>
                            <td>Assumes utility cannot be measured quantitatively but can be ranked qualitatively (e.g., preference).</td>
                          </tr>
                          <tr>
                            <td><strong>Key Proponent</strong></td>
                            <td>Developed primarily by Alfred Marshall.</td>
                            <td>Developed primarily by J.R. Hicks and R.G.D. Allen.</td>
                          </tr>
                          <tr>
                            <td><strong>Analytical Tool</strong></td>
                            <td>Uses Marginal Utility analysis.</td>
                            <td>Uses Indifference Curve analysis.</td>
                          </tr>
                        </tbody>
                      </table>`
                    }
                  ]
                }
              ]
            }
          ]
        }
      ]
    }
  ],
  pastPapers: [
    {
      year: 2025,
      subject: `Mathematics`,
      title: `ISC 2025 Class 12 Mathematics Board Examination`,
      questionPaperDownloadUrl: `https://example.com/downloads/isc-2025-maths-12.pdf`,
      markingSchemeSolutions: [
        {
          number: `Question 1`,
          section: `Section A`,
          text: `Solve the linear differential equation: $\\frac{dy}{dx} + y \\cot x = 2 \\cos x$.`,
          solution: `<strong>Marking-Scheme Solution:</strong><br><br>
          Given equation:<br>
          $$\\frac{dy}{dx} + y \\cot x = 2 \\cos x$$<br>
          This is a linear differential equation of the form $\\frac{dy}{dx} + Py = Q$, where:<br>
          $$P = \\cot x \\quad \\text{and} \\quad Q = 2 \\cos x$$<br><br>
          
          <strong>Step 1: Calculate the Integrating Factor (I.F.).</strong><br>
          $$\\text{I.F.} = e^{\\int P \\, dx} = e^{\\int \\cot x \\, dx}$$<br>
          Since $\\int \\cot x \\, dx = \\log(\\sin x)$:<br>
          $$\\text{I.F.} = e^{\\log(\\sin x)} = \\sin x$$<br>
          <span class="marking-scheme-badge">[Allocated Marks: 2]</span><br><br>
          
          <strong>Step 2: Solve the differential equation.</strong><br>
          The general solution is given by:<br>
          $$y \\cdot (\\text{I.F.}) = \\int Q \\cdot (\\text{I.F.}) \\, dx + C$$<br>
          $$y \\cdot \\sin x = \\int (2 \\cos x) \\cdot \\sin x \\, dx + C$$<br>
          Simplify the integral using the identity $2 \\sin x \\cos x = \\sin 2x$:<br>
          $$y \\cdot \\sin x = \\int \\sin 2x \\, dx + C$$<br>
          $$y \\cdot \\sin x = -\\frac{\\cos 2x}{2} + C$$<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span><br><br>
          
          <strong>Conclusion:</strong><br>
          The general solution is <strong>$y \\sin x = -\\frac{\\cos 2x}{2} + C$</strong>.<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span>`
        }
      ]
    },
    {
      year: 2024,
      subject: `Physics`,
      title: `ISC 2024 Class 12 Physics Board Examination`,
      questionPaperDownloadUrl: `https://example.com/downloads/isc-2024-physics-12.pdf`,
      markingSchemeSolutions: [
        {
          number: `Question 1`,
          section: `Section A`,
          text: `State Faraday's Laws of Electromagnetic Induction.`,
          solution: `<strong>Marking-Scheme Solution:</strong><br><br>
          Faraday's laws formulate the generation of electromotive force (emf) due to electromagnetic induction:<br><br>
          
          1. <strong>First Law:</strong> Whenever the magnetic flux linked with a closed circuit changes, an induced electromotive force (emf) is produced in the circuit. This induced emf lasts only as long as the change in magnetic flux continues.<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span><br><br>
          
          2. <strong>Second Law:</strong> The magnitude of the induced emf ($\\mathcal{E}$) in a circuit is directly proportional to the time rate of change of magnetic flux ($\\Phi_B$) linked with the circuit:<br>
          $$\\mathcal{E} = -\\frac{d\\Phi_B}{dt}$$<br>
          The negative sign indicates the direction of induced emf, which always opposes the change in magnetic flux producing it (Lenz's Law).<br>
          <span class="marking-scheme-badge">[Allocated Marks: 1]</span>`
        }
      ]
    },
    {
      year: 2023,
      subject: `Chemistry`,
      title: `ISC 2023 Class 12 Chemistry Board Examination`,
      questionPaperDownloadUrl: `https://example.com/downloads/isc-2023-chemistry-12.pdf`,
      markingSchemeSolutions: [
        {
          number: `Question 1`,
          section: `Section A`,
          text: `Differentiate between $S_N1$ and $S_N2$ reaction mechanisms in haloalkanes.`,
          solution: `<strong>Marking-Scheme Solution:</strong><br><br>
          The fundamental differences between nucleophilic substitution mechanisms are outlined as follows:<br><br>
          <table border="1" cellpadding="5" style="border-collapse: collapse; width:100%; text-align:left; border: 1px solid #ccc;">
            <thead>
              <tr style="background-color: #f1f5f9;">
                <th>Parameter</th>
                <th>$S_N1$ Mechanism</th>
                <th>$S_N2$ Mechanism</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Kinetics</strong></td>
                <td>First-order reaction: Rate depends only on substrate concentration.</td>
                <td>Second-order reaction: Rate depends on both substrate and nucleophile concentration.</td>
              </tr>
              <tr>
                <td><strong>Steps</strong></td>
                <td>Two-step process involving a carbocation intermediate.</td>
                <td>Single-step concerted process involving a transition state.</td>
              </tr>
              <tr>
                <td><strong>Stereochemistry</strong></td>
                <td>Leads to racemization (partial retention and inversion).</td>
                <td>Leads to complete inversion of configuration (Walden inversion).</td>
              </tr>
              <tr>
                <td><strong>Reactivity Order</strong></td>
                <td>Tertiary ($3^\\circ$) &gt; Secondary ($2^\\circ$) &gt; Primary ($1^\\circ$).</td>
                <td>Primary ($1^\\circ$) &gt; Secondary ($2^\\circ$) &gt; Tertiary ($3^\\circ$).</td>
              </tr>
            </tbody>
          </table><br>
          <span class="marking-scheme-badge">[Allocated Marks: 3 - 1 mark for each correct distinction]</span>`
        }
      ]
    }
  ]
};

export { Class9Data, Class10Data, Class11Data, Class12Data };